import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useT } from '../i18n';
import GalleryManager from './GalleryManager';
import { ITEM_KINDS } from '../constants/gallery';
import { useAuth } from '../context/AuthContext';
import { listGalleryItems } from '../firebase/galleryService';
import { fetchStats, SOCIAL_KINDS } from '../firebase/socialService';
import './DashboardGallery.css';

// ── DASHBOARD GALLERY ────────────────────────────────────────────────────────
// Two tabs over the same manager:
//
//   Img — photos the designer uploads straight from their device.
//   3D  — designs published from a project's own "..." menu in the workspace.
//
// The 3D tab has no upload button on purpose. A published design is tied to the
// project it came from (`projectId`), and that link only exists where the
// project does — so publishing happens there, and this tab is where you manage
// what was published: description, show/hide, delete.
//
// `photosOnly` drops the 3D half entirely, for a SHOP: it publishes products,
// never 3D scenes, so that tab could only ever be empty. With one kind left the
// tab strip is a heading, not a choice — see below.
export default function DashboardGallery({ openPhotoId, openDesignId, onOpened, photosOnly = false }) {
  const { t } = useT();
  const { user } = useAuth();
  // Open on the tab the deep-linked item lives in — a "liked your 3D design"
  // notification must not land on the photo tab.
  const [tab, setTab] = useState(
    openDesignId && !photosOnly ? ITEM_KINDS.DESIGN : ITEM_KINDS.PHOTO,
  );
  // The '+' lives beside the tabs (see the layout sketch), but the file input it
  // triggers belongs to GalleryManager, which owns compression and upload. This
  // ref is handed down so the button can open that input without duplicating any
  // of the logic behind it.
  const uploadRef = useRef(null);

  // Per-kind { count, likes }. The MOUNTED tab reports its own via onSummary;
  // the other one is fetched here, because only one GalleryManager exists at a
  // time and a tab whose total appears only after you click it looks broken.
  const [summary, setSummary] = useState({});

  // Which kinds have already been summarised. Toggling tabs re-ran the effect
  // and re-listed the now-hidden kind every single time; once fetched, the
  // mounted GalleryManager keeps its own side live via onSummary, so a second
  // fetch of the same kind can only ever return what we already have.
  const summarised = useRef(new Set());

  useEffect(() => {
    if (!user?.uid) return undefined;
    // Nothing hidden to summarise when there is only one kind — and listing the
    // design collection for a shop would be a read whose answer is always zero.
    if (photosOnly) return undefined;
    let alive = true;
    // Just the kind that ISN'T mounted — the visible one reports itself, and
    // fetching it twice would double the reads for nothing.
    const hidden = tab === ITEM_KINDS.PHOTO ? ITEM_KINDS.DESIGN : ITEM_KINDS.PHOTO;
    if (summarised.current.has(hidden)) return undefined;
    summarised.current.add(hidden);
    (async () => {
      try {
        const rows = await listGalleryItems(hidden, user.uid, true);
        const ids = rows
          .map(r => (hidden === ITEM_KINDS.PHOTO ? r.id : r.projectId))
          .filter(Boolean);
        const stats = ids.length
          ? await fetchStats(hidden === ITEM_KINDS.PHOTO ? SOCIAL_KINDS.PHOTO : SOCIAL_KINDS.PROJECT, ids)
          : {};
        if (!alive) return;
        setSummary(prev => ({
          ...prev,
          [hidden]: {
            count: rows.length,
            likes: ids.reduce((sum, id) => sum + (stats[id]?.likesCount || 0), 0),
          },
        }));
      } catch {
        // Allow a retry on the next switch — a failure that latched would
        // leave that tab's total permanently blank.
        summarised.current.delete(hidden);
      }
    })();
    return () => { alive = false; };
  }, [tab, user?.uid, photosOnly]);
  const onSummary = useCallback((kind, s) => {
    setSummary(prev => (
      prev[kind] && prev[kind].count === s.count && prev[kind].likes === s.likes
        ? prev                       // unchanged — don't re-render the strip
        : { ...prev, [kind]: s }
    ));
  }, []);

  const tabs = photosOnly
    // "Products", not "Img" — for a shop this strip names what the gallery
    // holds rather than which of two kinds you are looking at.
    ? [{ key: ITEM_KINDS.PHOTO, label: t('shop_tab_products') }]
    : [
      { key: ITEM_KINDS.PHOTO,  label: t('dg_tab_img') },
      { key: ITEM_KINDS.DESIGN, label: t('dg_tab_3d') },
    ];

  return (
    <div className="dg">
      <div className="dg-bar">
      <div className="dg-tabs" role="tablist" aria-label={t('dg_title')}>
        {tabs.map(x => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tab === x.key}
            className={`dg-tab${tab === x.key ? ' dg-tab--on' : ''}`}
            onClick={() => setTab(x.key)}
          >
            {x.label}
            {/* Total likes across this tab's work. Hidden at zero — an empty
                heart on a new gallery reads as a verdict, not an absence. */}
            {summary[x.key]?.likes > 0 && (
              <span className="dg-tab-likes" title={t('ps_likes')}>♥ {summary[x.key].likes}</span>
            )}
          </button>
        ))}
      </div>

      {/* Upload is only meaningful for photos — 3D designs arrive from a
          project's own menu, where the project is known. */}
      {tab === ITEM_KINDS.PHOTO && (
        <button
          type="button"
          className="dg-add"
          onClick={() => uploadRef.current?.()}
          aria-label={t('gal_add')}
          title={t('gal_add')}
        >+</button>
      )}
      </div>

      <div className="dg-panel" role="tabpanel">
        {/* Keyed so switching tabs remounts and refetches, rather than briefly
            showing the previous tab's items under the new heading. */}
        <GalleryManager
          key={tab}
          kind={tab}
          embedded
          allowUpload={false}
          onRequestUpload={fn => { uploadRef.current = fn; }}
          emptyHint={tab === ITEM_KINDS.DESIGN ? t('dg_3d_empty') : undefined}
          onSummary={onSummary}
          openItemId={tab === ITEM_KINDS.DESIGN ? openDesignId : openPhotoId}
          onOpened={onOpened}
        />
      </div>
    </div>
  );
}
